// Pure, isomorphic taxonomy helpers for the blog's tag/category pages. No DB and
// no Firebase param — safe to run on the SSR server and after client hydration.
// Tags/categories are stored as display strings ("permission loop"); the URL
// segment is the Hugo-style term slug ("permission-loop"), so the query layer
// slugifies stored terms and matches them against the requested segment.

export type TaxonomyKind = "tags" | "categories";

/** Display label for a kind — the single source shared by the routes, the
 *  taxonomy/term components, and the SEO heads so headings, link text, and page
 *  titles never disagree. */
export const TAXONOMY_LABEL: Record<TaxonomyKind, string> = {
  tags: "Tags",
  categories: "Categories",
};

/** A term with its URL slug and how many published posts carry it. */
export type TermSummary = { term: string; slug: string; count: number };

// A post shaped just enough to read its taxonomy arrays — decoupled from the DB
// row type so these helpers stay unit-testable without a database.
type Termed = Record<TaxonomyKind, string[]>;

/**
 * Hugo `urlize` for a taxonomy term: lowercase, collapse every run of
 * non-alphanumerics to a single hyphen, trim leading/trailing hyphens. Matches
 * the term URLs the legacy Hugo blog served (e.g. "permission loop" →
 * "permission-loop", "DKIM" → "dkim"). Returns "" only for a SYMBOL-ONLY term
 * (callers must guard — see the post-page chips in $slug.tsx).
 *
 * The twin it must stay identical to is `slugifyTerm` in
 * `functions/src/posts/terms.ts`, not imported because that module is
 * server-side and this one ships to the browser (see the header of
 * `src/lib/markdown.ts`). A theme renders its pills with the server copy and the
 * app-host route matches with this one, so a divergence is a pill pointing at a
 * 404.
 *
 * **It no longer matches `slugify` in `functions/src/posts/slug.ts`, and must
 * not be re-synced with it.** That one still folds every non-ASCII character
 * away, so a post titled "日本語" gets an empty slug — which is fine there and
 * was not fine here, because the two slugs are authored differently: a POST slug
 * is a seeded suggestion the author can see and edit, while a TAG slug is
 * derived and has no edit affordance at all. An author who cannot fix the URL
 * must not be given a tag that silently has none. Aligning them again would
 * re-break every non-Latin blog's tag pages.
 */
export function slugifyTerm(term: string): string {
  // Two rules, ASCII first. A term with no ASCII alphanumerics — 日本語,
  // Кириллица — used to slugify to "" and get no page at all, which read as a
  // rule about symbols but was a rule about the Latin alphabet. The Unicode
  // fallback is the same idea in every script; running it SECOND keeps every
  // slug that `/blog/tags/<slug>` already serves exactly where it is. See the
  // server twin in `functions/src/posts/terms.ts` for the full argument — the
  // two must stay identical or a pill points at a 404.
  const lower = term.toLowerCase();
  const ascii = lower.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  if (ascii) return ascii;
  return lower.replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "");
}

/**
 * Normalise a raw taxonomy array for rendering: trim each term, drop blanks, and
 * drop exact duplicates (first-seen order preserved). Render-time resilience for
 * legacy rows written before the write-time normalisation in
 * `functions/src/posts/handler.ts` — trimming keeps chip text consistent with the
 * URL slug, and de-duping stops duplicate `key={tag}` React collisions / repeated
 * chips on the blog list and post pages. Deliberately mirrors `normalizeTerms` in
 * `functions/src/posts/terms.ts` (client/server boundary — see the `slugifyTerm`
 * header on why the two aren't a shared import).
 */
export function normalizeTerms(terms: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of terms) {
    const term = raw.trim();
    if (!term || seen.has(term)) continue;
    seen.add(term);
    out.push(term);
  }
  return out;
}

function termsOf(post: Termed, kind: TaxonomyKind): string[] {
  return post[kind] ?? [];
}

/**
 * All distinct terms of a kind across the posts, each with its slug and post
 * count, sorted alphabetically by display term (Hugo's `.Alphabetical`). Terms
 * whose display forms collide on one slug are merged under the first-seen
 * display form — the same coalescing Hugo does.
 */
export function groupTerms(posts: Termed[], kind: TaxonomyKind): TermSummary[] {
  const bySlug = new Map<string, TermSummary>();
  for (const post of posts) {
    // Dedupe within the post first: a post whose terms collide on one slug
    // ("DKIM" and "dkim", or a literal duplicate) still carries that term once,
    // so it must contribute a single increment to keep `count` meaning "number
    // of posts that carry the term".
    const seenInPost = new Set<string>();
    for (const raw of termsOf(post, kind)) {
      const slug = slugifyTerm(raw);
      if (!slug || seenInPost.has(slug)) continue;
      seenInPost.add(slug);
      const existing = bySlug.get(slug);
      // Stored terms aren't trimmed at write-time for older rows, and the slug
      // already normalises whitespace — trim the display form to match so the
      // index never renders " permission loop " with stray spaces.
      if (existing) existing.count += 1;
      else bySlug.set(slug, { term: raw.trim(), slug, count: 1 });
    }
  }
  return [...bySlug.values()].sort((a, b) =>
    a.term.localeCompare(b.term, "en"),
  );
}

/**
 * The posts carrying a term whose slug matches `termSlug`, in the input order
 * (the query hands them over newest-first), plus the display form of the term.
 * Returns `undefined` when no post matches — the route turns that into a 404.
 */
export function filterByTerm<T extends Termed>(
  posts: T[],
  kind: TaxonomyKind,
  termSlug: string,
): { term: string; posts: T[] } | undefined {
  const matched: T[] = [];
  let term: string | undefined;
  for (const post of posts) {
    const hit = termsOf(post, kind).find((t) => slugifyTerm(t) === termSlug);
    if (hit) {
      // Trim the display form (the slug already normalises whitespace) so the
      // term-page heading and SEO title stay consistent with the URL slug.
      term ??= hit.trim();
      matched.push(post);
    }
  }
  return term ? { term, posts: matched } : undefined;
}
